import { getSupabase, isSupabaseConfigured, supabase } from '../config/supabase'
import { kvGet, kvSet, kvRemove } from '../infrastructure/storage/kvAccess'
import { shouldAllowSupabaseNetwork } from '../utils/supabaseReachability'
import { serializeBusinessTypeForCloud } from '../utils/businessTypeCloud'
import { normalizeBusinessStorageId } from '../utils/sharedCore'

export { isSupabaseConfigured }

// Get Supabase URL and key from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

/** Readable message from Auth / PostgREST / generic thrown values */
function extractSupabaseErrorMessage(error) {
  if (error == null) return ''
  if (typeof error === 'string') return error
  const msg = error.message
  if (typeof msg === 'string' && msg.trim()) return msg.trim()
  const desc = error.error_description || error.msg
  if (typeof desc === 'string' && desc.trim()) return desc.trim()
  const details = error.details
  if (typeof details === 'string' && details.trim()) return details.trim()
  const hint = error.hint
  if (typeof hint === 'string' && hint.trim()) return hint.trim()
  const code = error.code
  if (typeof code === 'string' && code.trim()) return code.trim()
  try {
    return JSON.stringify(error)
  } catch (_) {
    return String(error)
  }
}

/**
 * Maps fetch/DNS failures (e.g. net::ERR_NAME_NOT_RESOLVED) to a clear message.
 * Safe to pass Auth / PostgREST error objects: non-network errors pass through unchanged.
 */
function formatSupabaseConnectionError(error) {
  const raw = extractSupabaseErrorMessage(error)
  const lower = raw.toLowerCase()
  const code = String(error?.code || '').toLowerCase()

  // Auth blocked until email is confirmed (Supabase Dashboard setting)
  if (
    code === 'email_not_confirmed' ||
    lower.includes('email not confirmed') ||
    lower.includes('email_not_confirmed')
  ) {
    return (
      'E-mail non confirmé. Dans Supabase → Authentication → Providers → Email : ' +
      'désactivez « Confirm email ». Puis Authentication → Users → utilisateur → Confirm user ' +
      '(ou recréez le compte), et reconnectez-vous.'
    )
  }

  const isNetwork =
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('load failed') ||
    lower.includes('name not resolved') ||
    lower.includes('err_name_not_resolved') ||
    lower.includes('network request failed') ||
    (error && error.name === 'TypeError' && lower.includes('fetch'))

  if (!isNetwork) {
    return raw || 'Erreur inconnue'
  }

  let hostHint = ''
  try {
    if (supabaseUrl && /^https?:\/\//i.test(String(supabaseUrl))) {
      hostHint = ` (${new URL(supabaseUrl).host})`
    }
  } catch (_) {
    /* invalid REACT_APP_SUPABASE_URL */
  }

  return (
    'Impossible de joindre Supabase (réseau ou DNS)' +
    hostHint +
    '. Vérifiez la connexion internet, le VPN/pare-feu, et que REACT_APP_SUPABASE_URL dans le fichier .env ' +
    'correspond exactement à l’URL du projet (Supabase → Project Settings → API → Project URL). ' +
    'Redémarrez le serveur de développement après toute modification du .env.'
  )
}

// Authentication functions
export const authService = {
  async adminSignIn(username, password) {
    const explicitlyEnabled = process.env.REACT_APP_ENABLE_TEMP_ADMIN_LOGIN === 'true';
    const explicitlyDisabled = process.env.REACT_APP_ENABLE_TEMP_ADMIN_LOGIN === 'false';
    const isEnabled = explicitlyDisabled ? false : explicitlyEnabled;

    if (!isEnabled) {
      return { success: false, error: 'Temporary admin login is disabled' };
    }

    const adminUsername = (process.env.REACT_APP_TEMP_ADMIN_USERNAME || '').trim();
    const adminPassword = process.env.REACT_APP_TEMP_ADMIN_PASSWORD || '';

    if (!adminUsername || !adminPassword) {
      return {
        success: false,
        error: 'Temporary admin login is not configured (set REACT_APP_TEMP_ADMIN_USERNAME and REACT_APP_TEMP_ADMIN_PASSWORD)'
      };
    }

    const normalizedInput = (username || '').trim();
    if (normalizedInput.toLowerCase() !== adminUsername.toLowerCase() || password !== adminPassword) {
      return { success: false, error: 'Invalid admin credentials' };
    }

    return {
      success: true,
      type: 'admin',
      data: {
        user: {
          id: 'admin-temp-user',
          email: (process.env.REACT_APP_TEMP_ADMIN_EMAIL || '').trim() || 'admin@local.temp'
        }
      },
      temporary: true
    };
  },

  /**
   * After the in-app “temp admin” (LSS) gate, opens a real Supabase Auth session so
   * PostgREST/RLS can return rows from `businesses` in AdminDashboard.
   * Create an Auth user, add it to `admin_users` (auth_user_id), then set:
   * REACT_APP_SUPABASE_ADMIN_EMAIL / REACT_APP_SUPABASE_ADMIN_PASSWORD in `.env` and rebuild.
   *
   * Note: TEMP_ADMIN_* is only the local app gate — it does NOT create a Supabase Auth user.
   * SUPABASE_ADMIN_* must match a real Auth user (email+password) that exists under Authentication → Users.
   */
  async signInSupabaseAdminForDashboard() {
    try {
      const email = (process.env.REACT_APP_SUPABASE_ADMIN_EMAIL || '').trim()
      const password = String(process.env.REACT_APP_SUPABASE_ADMIN_PASSWORD || '').trim()
      if (!email || !password) {
        return { success: true, skipped: true }
      }

      // Admin UI must never be blocked by short offline cooldowns
      const { forceAllowSupabaseNetwork, markSupabaseReachable } = await import(
        '../utils/supabaseReachability'
      )
      forceAllowSupabaseNetwork()

      // Do not signOut first — a failed admin login must not disturb the local
      // temp-admin session / race with auth listeners. Overwrite session via sign-in.
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        const msg = String(error.message || error)
        const lower = msg.toLowerCase()
        const invalidCreds =
          lower.includes('invalid login') ||
          lower.includes('invalid credentials') ||
          lower.includes('invalid_grant') ||
          error?.status === 400

        // Proven network answer — never treat as offline cooldown
        markSupabaseReachable()

        console.warn('[Admin] Supabase admin sign-in failed:', msg)
        return {
          success: false,
          errorCode: invalidCreds ? 'INVALID_ADMIN_CREDENTIALS' : 'ADMIN_SIGNIN_FAILED',
          email,
          error: invalidCreds
            ? 'Invalid login credentials — REACT_APP_SUPABASE_ADMIN_EMAIL/PASSWORD must match a real Auth user (not only TEMP_ADMIN). Create the user under Authentication → Users, set the same password, then insert into admin_users.'
            : formatSupabaseConnectionError(error)
        }
      }

      const authUserId = data?.user?.id
      if (!authUserId) {
        return { success: false, errorCode: 'ADMIN_SESSION_EMPTY', error: 'admin_session_empty' }
      }

      // RLS: businesses SELECT for all rows requires public.admin_users.auth_user_id = auth.uid()
      const { data: adminRow, error: adminErr } = await supabase
        .from('admin_users')
        .select('id, auth_user_id')
        .eq('auth_user_id', authUserId)
        .maybeSingle()

      if (adminErr) {
        console.warn('[Admin] admin_users lookup failed:', adminErr.message)
        return {
          success: false,
          errorCode: 'ADMIN_USERS_LOOKUP_FAILED',
          error: formatSupabaseConnectionError(adminErr)
        }
      }

      if (!adminRow) {
        console.warn(
          '[Admin] Auth OK but no admin_users row — RLS hides all businesses (including pending).',
          { email, authUserId }
        )
        return {
          success: false,
          errorCode: 'NOT_IN_ADMIN_USERS',
          authUserId,
          email,
          error:
            'Compte Auth connecté, mais absent de la table admin_users. Insérez auth_user_id dans admin_users (SQL Editor Supabase).'
        }
      }

      markSupabaseReachable()
      return { success: true, authUserId, email }
    } catch (error) {
      const name = error?.name || ''
      if (name === 'SupabaseOfflineError' || name === 'AbortError') {
        // One more chance: clear cooldown and surface a clear message
        try {
          const { forceAllowSupabaseNetwork } = await import('../utils/supabaseReachability')
          forceAllowSupabaseNetwork()
        } catch {
          /* optional */
        }
      }
      console.warn('[Admin] Supabase admin sign-in error:', error)
      return {
        success: false,
        errorCode: 'ADMIN_SIGNIN_EXCEPTION',
        error: formatSupabaseConnectionError(error)
      }
    }
  },

  // Business registration
  async registerBusiness(businessData) {
    try {
      const cloudBusinessType = serializeBusinessTypeForCloud(businessData.business_type)
      const localBusinessIdRaw =
        businessData.id && String(businessData.id).trim()
          ? String(businessData.id).trim()
          : null
      const localBusinessId = localBusinessIdRaw
        ? normalizeBusinessStorageId(localBusinessIdRaw) || localBusinessIdRaw
        : null

      // Create auth user — metadata also feeds DB trigger (0002_auth_business_trigger.sql)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: businessData.email,
        password: businessData.password,
        options: {
          data: {
            business_id: localBusinessId,
            business_name: businessData.business_name,
            owner_name: businessData.owner_name,
            username: businessData.username,
            nif: businessData.nif || null,
            phone: businessData.phone || null,
            address: businessData.address || null,
            // Never put manager PIN in auth metadata (readable from JWT)
            business_type: cloudBusinessType
          }
        }
      })

      if (authError) {
        const authMsg = formatSupabaseConnectionError(authError)
        const lower = String(authMsg || '').toLowerCase()
        const alreadyRegistered =
          lower.includes('already registered') ||
          lower.includes('already been registered') ||
          lower.includes('user already exists') ||
          lower.includes('email address is already')

        if (alreadyRegistered && businessData.email && businessData.password) {
          // Re-register / upgrade retries: sign in (even if pending) and return owned row
          const existing = await authService.businessSignIn(
            businessData.username || businessData.email,
            businessData.password,
            { allowPending: true }
          )
          if (existing.success && existing.business) {
            return {
              success: true,
              reusedExisting: true,
              pending: Boolean(existing.pending),
              data: existing.business
            }
          }
        }

        return { success: false, error: authMsg }
      }

      if (!authData?.user?.id) {
        return {
          success: false,
          error:
            'Compte utilisateur non créé (réponse vide). Vérifiez la confirmation e-mail dans Supabase Auth ou les quotas du projet.'
        }
      }

      const hasSession = Boolean(authData.session?.access_token)
      if (!hasSession) {
        // Email confirmation on → no JWT. Trigger may still create the pending row.
        // Soft success: Auth user exists; local app can proceed offline.
        return {
          success: true,
          pendingEmailConfirmation: true,
          data: {
            id: localBusinessId,
            auth_user_id: authData.user.id,
            status: 'pending'
          },
          error:
            'Compte Auth créé — confirmation e-mail requise. Compte local actif; validation admin après confirmation.'
        }
      }

      // Create business record — store PBKDF2 hash of manager PIN (never plaintext)
      let managerCodeHash = null
      const rawManager = businessData.manager_code
      if (rawManager != null && String(rawManager).trim()) {
        try {
          const { isPbkdf2PasswordHash, hashPasswordPbkdf2 } = await import('../utils/passwordKdf')
          const raw = String(rawManager).trim()
          managerCodeHash = isPbkdf2PasswordHash(raw)
            ? raw
            : await hashPasswordPbkdf2(raw)
        } catch {
          managerCodeHash = null
        }
      }

      const businessRecord = {
        ...(localBusinessId ? { id: localBusinessId } : {}),
        auth_user_id: authData.user.id,
        business_name: businessData.business_name,
        owner_name: businessData.owner_name,
        nif: businessData.nif,
        phone: businessData.phone,
        address: businessData.address,
        email: businessData.email,
        username: businessData.username,
        manager_code: managerCodeHash,
        business_type: cloudBusinessType,
        logo: businessData.logo?.preview || businessData.logo || null,
        status: 'pending',
        subscription_amount: null,
        created_at: new Date().toISOString()
      }

      const { data: businessDbData, error: businessError } = await supabase
        .from('businesses')
        .insert(businessRecord)
        .select()
        .single()

      if (businessError) {
        // Trigger may have inserted already (unique auth_user_id) with manager_code null
        const { data: existing, error: selectError } = await supabase
          .from('businesses')
          .select('*')
          .eq('auth_user_id', authData.user.id)
          .maybeSingle()

        if (!selectError && existing) {
          const patch = {
            updated_at: new Date().toISOString()
          }
          if (cloudBusinessType) patch.business_type = cloudBusinessType
          if (managerCodeHash) patch.manager_code = managerCodeHash
          if (Object.keys(patch).length > 1) {
            await supabase
              .from('businesses')
              .update(patch)
              .eq('auth_user_id', authData.user.id)
          }
          return {
            success: true,
            data: { ...existing, ...patch, manager_code: managerCodeHash || existing.manager_code }
          }
        }

        return { success: false, error: formatSupabaseConnectionError(businessError) }
      }

      // Generate and store unlock code immediately after business creation
      try {
        const { unlockCodeService } = await import('./unlockCodeService');
        const unlockCodeResult = await unlockCodeService.generateAndStoreUnlockCode(businessDbData.id);
        
        if (unlockCodeResult.success) {
          // Update the business data to include the unlock code
          businessDbData.unlock_code = unlockCodeResult.unlockCode;
          console.log(`Unlock code generated for new business: ${unlockCodeResult.unlockCode}`);
        } else {
          console.warn('Failed to generate unlock code for new business:', unlockCodeResult.error);
        }
      } catch (unlockCodeError) {
        console.warn('Error generating unlock code for new business:', unlockCodeError);
        // Don't fail the business creation if unlock code generation fails
      }

      return { success: true, data: businessDbData }
    } catch (error) {
      return { success: false, error: formatSupabaseConnectionError(error) }
    }
  },

  // Business sign in
  // options.allowPending — upgrade / link paths need JWT + row even if still pending admin
  async businessSignIn(username, password, options = {}) {
    try {
      const login = String(username || '').trim();
      const plain = String(password || '').trim();
      const allowPending = Boolean(options.allowPending);
      if (!login || !plain) {
        return { success: false, error: 'Identifiant ou mot de passe manquant' };
      }

      await supabase.auth.signOut();

      // Resolve Auth email: email login, local catalog, or public approved row by username
      let email = login.includes('@') ? login.toLowerCase() : null;
      let preliminaryBusiness = null;

      if (!email) {
        try {
          const { offlineAuthService } = await import('./offlineAuthService');
          const normalized = login.toLowerCase();
          const local = (offlineAuthService.getBusinesses() || []).find((b) => {
            const u = String(b.username || '')
              .trim()
              .toLowerCase();
            const e = String(b.email || '')
              .trim()
              .toLowerCase();
            return u === normalized || e === normalized;
          });
          if (local?.email) {
            email = String(local.email).trim().toLowerCase();
          }
        } catch {
          /* optional */
        }
      }

      if (!email) {
        // Anon may only see approved rows
        const { data: byUser, error: byUserErr } = await supabase
          .from('businesses')
          .select('*')
          .ilike('username', login)
          .eq('status', 'approved')
          .limit(1);

        if (byUserErr) {
          return { success: false, error: formatSupabaseConnectionError(byUserErr) };
        }
        if (byUser?.length) {
          preliminaryBusiness = byUser[0];
          email = String(byUser[0].email || '')
            .trim()
            .toLowerCase();
        }
      }

      if (!email) {
        // Last public try: email as username field itself
        const { data: byEmail } = await supabase
          .from('businesses')
          .select('*')
          .ilike('email', login)
          .eq('status', 'approved')
          .limit(1);
        if (byEmail?.length) {
          preliminaryBusiness = byEmail[0];
          email = String(byEmail[0].email || '')
            .trim()
            .toLowerCase();
        }
      }

      if (!email) {
        return {
          success: false,
          error:
            'Compte introuvable ou non encore approuvé par l’administrateur. Utilisez l’e-mail ou le nom d’utilisateur d’inscription.'
        };
      }

      const { data: authResult, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password: plain
      });

      if (authError) {
        return { success: false, error: formatSupabaseConnectionError(authError) };
      }

      const authUserId = authResult?.user?.id;
      // With JWT, owner sees own row even if pending/suspended (RLS)
      let business = preliminaryBusiness;
      if (authUserId) {
        const { data: owned } = await supabase
          .from('businesses')
          .select('*')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        if (owned) business = owned;
      }

      if (!business) {
        await supabase.auth.signOut();
        return {
          success: false,
          error:
            'Compte Auth trouvé, mais aucune fiche entreprise en base — contactez l’administrateur AMAN.'
        };
      }

      if (business.status && business.status !== 'approved') {
        if (allowPending && business.status === 'pending') {
          return {
            success: true,
            pending: true,
            data: authResult,
            business
          };
        }
        await supabase.auth.signOut();
        return {
          success: false,
          error:
            business.status === 'pending'
              ? 'Compte cloud en attente d’approbation admin. Connexion locale possible sur le PC d’installation.'
              : 'Compte suspendu ou non approuvé. Contactez l’administrateur AMAN.'
        };
      }

      return { success: true, data: authResult, business };
    } catch (error) {
      return { success: false, error: formatSupabaseConnectionError(error) };
    }
  },

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Signout error:', error)
      return { success: false, error: formatSupabaseConnectionError(error) }
    }
  },

  // Get current user
  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) throw error
      
      if (!user) return { success: true, user: null }

      // Check if it's an admin
      const { data: adminUser } = await supabase
        .from('admin_users')
        .select('*')
        .eq('auth_user_id', user.id)
        .single()

      if (adminUser) {
        return { success: true, user, profile: adminUser, type: 'admin' }
      }

      // Check if it's a business
      const { data: business } = await supabase
        .from('businesses')
        .select('*')
        .eq('auth_user_id', user.id)
        .single()

      if (business) {
        return { success: true, user, profile: business, type: 'business' }
      }

      return { success: true, user, type: 'unknown' }
    } catch (error) {
      console.error('Get user error:', error)
      return { success: false, error: error.message }
    }
  },

  // Listen to auth changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  },

  // Debug function to list all businesses
  async debugListAllBusinesses() {
    try {
      const { data: businesses, error } = await supabase
        .from('businesses')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching businesses:', error)
        return { success: false, error: formatSupabaseConnectionError(error) }
      }

      return { success: true, data: businesses }
    } catch (error) {
      console.error('Debug list businesses error:', error)
      return { success: false, error: formatSupabaseConnectionError(error) }
    }
  }
}

// Business management functions for admin
export const businessService = {
  // Get all businesses (admin only — requires admin_users membership under RLS)
  async getAllBusinesses() {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Empty array with no error is typical RLS filter (session is shop owner, or not in admin_users)
      if (!data || data.length === 0) {
        try {
          const {
            data: { user }
          } = await supabase.auth.getUser()
          if (user?.id) {
            const { data: adminRow } = await supabase
              .from('admin_users')
              .select('id')
              .eq('auth_user_id', user.id)
              .maybeSingle()
            if (!adminRow) {
              return {
                success: false,
                errorCode: 'NOT_IN_ADMIN_USERS',
                authUserId: user.id,
                error:
                  'Session Supabase active, mais pas d’entrée admin_users — RLS renvoie 0 lignes (même si businesses contient des données).'
              }
            }
          } else {
            return {
              success: false,
              errorCode: 'NO_SESSION',
              error: 'Aucune session Supabase admin — impossible de lire businesses (RLS).'
            }
          }
        } catch {
          /* keep empty success if diagnosis fails */
        }
      }

      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Get businesses error:', error)
      return { success: false, error: formatSupabaseConnectionError(error) }
    }
  },

  /** Patch business_type from local registration (owner session). */
  async updateBusinessType(businessId, businessType) {
    try {
      const serialized = serializeBusinessTypeForCloud(businessType)
      if (!serialized || !businessId) {
        return { success: false, error: 'Invalid business type or id' }
      }

      const { data, error } = await supabase
        .from('businesses')
        .update({
          business_type: serialized,
          updated_at: new Date().toISOString()
        })
        .eq('id', businessId)
        .select()
        .single()

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Update business type error:', error)
      return { success: false, error: formatSupabaseConnectionError(error) }
    }
  },

  // Get business status (for monitoring)
  // businesses.id is TEXT (local slug or UUID) — never UUID-only
  async getBusinessStatus(businessId) {
    try {
      if (!businessId || typeof businessId !== 'string') {
        return { success: false, error: 'Invalid business ID', skipRetry: true };
      }

      const { isValidCloudBusinessId } = await import('./cloudCapability');
      if (!isValidCloudBusinessId(businessId) || businessId === 'admin_user') {
        return {
          success: false,
          error: 'Admin user - skipping business status check',
          skipRetry: true
        };
      }

      let remoteId = businessId;
      try {
        const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
        remoteId = resolveSupabaseBusinessId(businessId) || businessId;
      } catch {
        remoteId = businessId;
      }

      if (!shouldAllowSupabaseNetwork()) {
        return { success: false, error: 'Offline', skipRetry: true };
      }

      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const queryPromise = supabase
        .from('businesses')
        .select('id, business_name, status, suspended_date, suspended_by, cloud_plan')
        .eq('id', remoteId)
        .maybeSingle();

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error) {
        const msg = String(error.message || '');
        if (
          msg.includes('Failed to fetch') ||
          msg.includes('ERR_CONNECTION_CLOSED') ||
          msg.includes('NetworkError') ||
          msg.includes('Request timeout')
        ) {
          return { success: false, error: 'Connection issue', skipRetry: true };
        }
        throw error;
      }

      if (!data) {
        return { success: false, error: 'not_found', skipRetry: true };
      }

      return { success: true, data };
    } catch (error) {
      const msg = String(error?.message || error || '');
      if (
        msg.includes('Failed to fetch') ||
        msg.includes('ERR_CONNECTION_CLOSED') ||
        msg.includes('NetworkError') ||
        msg.includes('Request timeout')
      ) {
        return { success: false, error: 'Connection issue', skipRetry: true };
      }
      console.error('Get business status error:', error);
      return { success: false, error: msg };
    }
  },

  // Update business status (admin only)
  async updateBusinessStatus(businessId, status, adminUsername = 'LSS') {
    try {
      const { isValidCloudBusinessId } = await import('./cloudCapability');
      if (!isValidCloudBusinessId(businessId)) {
        return { success: false, error: 'Invalid business ID' };
      }

      const updateData = { 
        status,
        updated_at: new Date().toISOString()
      }

      if (status === 'approved') {
        updateData.activated_date = new Date().toISOString()
        updateData.activated_by = adminUsername
      } else if (status === 'suspended') {
        updateData.suspended_date = new Date().toISOString()
        updateData.suspended_by = adminUsername
      }

      const { data, error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId)
        .select()

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Update business status error:', error)
      return { success: false, error: error.message }
    }
  },

  // Update business with subscription details (admin only)
  // cloudPlan: 'none' | 'mirror' | 'hybrid' — Phase 1 Mirror add-on gate
  async updateBusinessWithSubscription(
    businessId,
    status,
    subscriptionType,
    customAmount,
    adminUsername = 'LSS',
    cloudPlan = 'none'
  ) {
    try {
      const { isValidCloudBusinessId, normalizeCloudPlan } = await import('./cloudCapability');
      if (!isValidCloudBusinessId(businessId)) {
        return { success: false, error: 'Invalid business ID' };
      }

      console.log('🔧 updateBusinessWithSubscription called with:', {
        businessId,
        status,
        subscriptionType,
        customAmount,
        adminUsername,
        cloudPlan
      });
      
      const currentDate = new Date();
      let expiryDate;
      let subscriptionAmount;
      
      // Use custom amount if provided, otherwise use defaults
      if (customAmount && !isNaN(parseFloat(customAmount))) {
        subscriptionAmount = parseFloat(customAmount);
      } else {
        // Default amounts if no custom amount provided
        if (subscriptionType === 'monthly') {
          subscriptionAmount = 50.00; // 50 MRU for monthly
        } else if (subscriptionType === 'yearly' || subscriptionType === 'annual') {
          subscriptionAmount = 500.00; // 500 MRU for yearly/annual
        }
      }
      
      if (subscriptionType === 'monthly') {
        expiryDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate());
      } else if (subscriptionType === 'yearly' || subscriptionType === 'annual') {
        expiryDate = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate());
      }

      // Convert 'yearly' to 'annual' to match database constraint
      const dbSubscriptionType = subscriptionType === 'yearly' ? 'annual' : subscriptionType;
      const dbCloudPlan = normalizeCloudPlan(cloudPlan);
      
      console.log('🔄 Converting subscription type:', { original: subscriptionType, database: dbSubscriptionType });
      
      const updateData = { 
        status,
        subscription_type: dbSubscriptionType,
        subscription_amount: subscriptionAmount,
        subscription_expiry_date: expiryDate.toISOString(),
        cloud_plan: dbCloudPlan,
        updated_at: new Date().toISOString()
      }

      if (status === 'approved') {
        updateData.activated_date = currentDate.toISOString()
        updateData.activated_by = adminUsername
      } else if (status === 'suspended') {
        updateData.suspended_date = currentDate.toISOString()
        updateData.suspended_by = adminUsername
      }

      console.log('📝 Update data:', updateData);

      // First, update the business
      const { error: updateError } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId)

      console.log('📊 Update response:', { updateError });

      if (updateError) throw updateError

      // Then, fetch the updated business data separately
      const { data, error: selectError } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', businessId)
        .single()

      console.log('📊 Select response:', { data, selectError });

      if (selectError) {
        console.warn('⚠️ Could not fetch updated business data, but update succeeded');
        // Return success even if we can't fetch the data, since the update succeeded
        return { success: true, data: null }
      }

      return { success: true, data: [data] }
    } catch (error) {
      console.error('❌ Update business with subscription error:', error)
      return { success: false, error: error.message }
    }
  },

  /**
   * Phase 3 — change cloud_plan on an existing business (admin).
   * @param {string} businessId
   * @param {'none'|'mirror'|'hybrid'} cloudPlan
   */
  async updateCloudPlan(businessId, cloudPlan) {
    try {
      const { isValidCloudBusinessId, normalizeCloudPlan } = await import('./cloudCapability');
      if (!isValidCloudBusinessId(businessId)) {
        return { success: false, error: 'Invalid business ID' };
      }
      const dbCloudPlan = normalizeCloudPlan(cloudPlan);
      const { data, error } = await supabase
        .from('businesses')
        .update({
          cloud_plan: dbCloudPlan,
          updated_at: new Date().toISOString()
        })
        .eq('id', businessId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Update cloud_plan error:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete business (admin only)
  async deleteBusiness(businessId) {
    try {
      // First, get the business details
      const { data: business, error: fetchError } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', businessId)
        .single()

      if (fetchError) {
        return { success: false, error: fetchError.message }
      }

      // Delete the business record
      const { error: deleteError } = await supabase
        .from('businesses')
        .delete()
        .eq('id', businessId)

      if (deleteError) {
        return { success: false, error: deleteError.message }
      }

      return { success: true, message: 'Business deleted successfully' }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }
}

// Reports syncing functions
export const reportsService = {
  // Sync local report data to Supabase
  async syncReports(businessId, reportData) {
    try {
      // Validate business ID format
      if (!businessId || typeof businessId !== 'string') {
        console.warn('⚠️ Invalid business ID format:', businessId);
        return { success: false, error: 'Invalid business ID format' };
      }

      const { assertMirrorSyncAllowed } = await import('./cloudCapability');
      const gate = assertMirrorSyncAllowed(businessId);
      if (!gate.allowed) {
        // Same contract as sale/purchase: local OK, outbox may retry later
        return { success: true, localOnly: true, reason: gate.reason };
      }

      const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
      const remoteId = resolveSupabaseBusinessId(businessId) || businessId;

      const reportRecord = {
        business_id: remoteId,
        report_type: 'monthly', // or determine based on data
        report_period: new Date().toISOString().split('T')[0], // Today's date
        report_data: reportData,
        metrics: this.extractMetrics(reportData),
        sync_timestamp: new Date().toISOString()
      }

      // Check if report already exists for this period
      const { data: existingReport, error: checkError } = await supabase
        .from('business_reports')
        .select('id')
        .eq('business_id', remoteId)
        .eq('report_period', reportRecord.report_period)
        .eq('report_type', reportRecord.report_type)
        .single()

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.warn('⚠️ Error checking existing report:', checkError);
        // Continue with insert if check fails
      }

      let result
      if (existingReport) {
        // Update existing report
        const { data, error } = await supabase
          .from('business_reports')
          .update(reportRecord)
          .eq('id', existingReport.id)
          .select()

        if (error) {
          console.error('❌ Error updating report:', error);
          throw error;
        }
        result = { data, updated: true }
      } else {
        // Insert new report
        const { data, error } = await supabase
          .from('business_reports')
          .insert(reportRecord)
          .select()

        if (error) {
          console.error('❌ Error inserting report:', error);
          throw error;
        }
        result = { data, updated: false }
      }

      return { success: true, ...result }
    } catch (error) {
      console.error('❌ Sync reports error:', error);
      
      // Handle specific error types
      if (error.code === '406' || error.message?.includes('Not Acceptable')) {
        return { success: false, error: 'Table schema incompatible', code: 'SCHEMA_ERROR' };
      }
      
      return { success: false, error: error.message, code: error.code }
    }
  },

  // Extract key metrics from report data
  extractMetrics(reportData) {
    return {
      totalSales: reportData.totalSales || 0,
      totalRevenue: reportData.totalRevenue || 0,
      totalProducts: reportData.totalProducts || 0,
      totalCustomers: reportData.totalCustomers || 0,
      lastSyncDate: new Date().toISOString()
    }
  },

  // Get reports for admin dashboard
  async getAllReports(dateRange = null) {
    try {
      let query = supabase
        .from('business_reports')
        .select(`
          *,
          businesses (
            business_name,
            owner_name,
            business_type,
            status
          )
        `)
        .order('sync_timestamp', { ascending: false })

      if (dateRange) {
        query = query
          .gte('report_period', dateRange.start)
          .lte('report_period', dateRange.end)
      }

      const { data, error } = await query

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Get all reports error:', error)
      return { success: false, error: error.message }
    }
  },

  // Get reports for specific business
  async getBusinessReports(businessId) {
    try {
      const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
      const remoteId = resolveSupabaseBusinessId(businessId) || businessId;
      const { data, error } = await supabase
        .from('business_reports')
        .select('*')
        .eq('business_id', remoteId)
        .order('report_period', { ascending: false })

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Get business reports error:', error)
      return { success: false, error: error.message }
    }
  },

  /**
   * Build 28-day admin report from synced Supabase payload.
   */
  async generate28DayReport(businessId, businessInfo = null) {
    try {
      const { generate28DayReport: buildReport } = await import('./adminReportService');
      const data = await buildReport(businessId, businessInfo);
      if (!data) {
        return { success: false, error: 'No report data synced for this business' };
      }
      return { success: true, data };
    } catch (error) {
      console.error('Generate 28-day report error:', error);
      return { success: false, error: error.message };
    }
  }
}

// Soft UI hint only — cloud ops must probe/fetch, not gate on this alone.
export const isOnline = () => {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
};

/** REST probe (respects reachability cooldown). */
export async function canReachSupabase() {
  if (!isSupabaseConfigured() || !shouldAllowSupabaseNetwork()) {
    return false;
  }
  try {
    const response = await fetch(`${supabaseUrl}/rest/v1/businesses?select=id&limit=1`, {
      method: 'GET',
      headers: {
        apikey: supabaseKey,
        Authorization: `Bearer ${supabaseKey}`,
        'Content-Type': 'application/json'
      }
    });
    return response.ok || (response.status >= 400 && response.status < 500);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Supabase] probe failed:', error?.message || error);
    }
    return false;
  }
}

function storeOrderLocally(orderData = {}) {
  const offlineOrders = getOfflineOrdersArray();
  const orderKey =
    orderData.orderId ||
    orderData.order_id ||
    `ORD${Math.floor(Math.random() * 90000) + 10000}`;
  const idx = offlineOrders.findIndex(
    (o) =>
      String(o.orderId || o.order_id || '') === String(orderKey) ||
      (orderData.id != null && String(o.id) === String(orderData.id))
  );
  const newOrder = {
    ...(idx >= 0 ? offlineOrders[idx] : {}),
    ...orderData,
    id: orderData.id || (idx >= 0 ? offlineOrders[idx].id : Date.now()),
    orderId: orderKey,
    status: orderData.status || (idx >= 0 ? offlineOrders[idx].status : 'en_route'),
    orderDate:
      orderData.orderDate ||
      orderData.order_date ||
      (idx >= 0 ? offlineOrders[idx].orderDate : null) ||
      new Date().toISOString().split('T')[0],
    synced: false,
    updatedAt: new Date().toISOString()
  };
  if (idx >= 0) offlineOrders[idx] = newOrder;
  else offlineOrders.push(newOrder);
  setOfflineOrdersArray(offlineOrders);
  return newOrder;
}

async function enqueueOrderSync(businessId, order, op = 'upsert') {
  const biz = businessId || order?.business_id || order?.businessId;
  if (!biz || !order) return null;
  try {
    const { enqueueOutbox, buildOrderSyncOutboxEntry } = await import(
      '../infrastructure/storage/outboxAccess'
    );
    const entry = buildOrderSyncOutboxEntry(biz, order, op);
    if (!entry) return null;
    const row = await enqueueOutbox(entry);
    void import('./parallelCloudFieldSync')
      .then(({ scheduleAutomaticCloudSync }) => {
        scheduleAutomaticCloudSync(biz, { reason: 'order_sync' });
      })
      .catch(() => {});
    return row;
  } catch (e) {
    console.warn('[order_sync] enqueue failed:', e?.message || e);
    return null;
  }
}

function getOfflineOrdersArray() {
  const v = kvGet('offline_orders');
  return Array.isArray(v) ? v : [];
}

function setOfflineOrdersArray(orders) {
  kvSet('offline_orders', orders);
}

function getOfflineConversionsArray() {
  const v = kvGet('offline_order_conversions');
  return Array.isArray(v) ? v : [];
}

function setOfflineConversionsArray(conversions) {
  kvSet('offline_order_conversions', conversions);
}

// Reports: outbox is SoT; memory is a soft view of pending report_sync only.
let offlineQueue = []
let isSyncing = false // Flag to prevent multiple simultaneous syncs

export const offlineService = {
  /**
   * Enqueue report to IndexedDB outbox only (ADR-0017 — no second queue SoT).
   * Memory cache refreshes after enqueue resolves (status UI only).
   */
  queueReport(businessId, reportData) {
    const item = {
      businessId,
      reportData,
      timestamp: new Date().toISOString()
    };
    Promise.resolve()
      .then(async () => {
        const { enqueueOutbox } = await import('../infrastructure/storage/outboxAccess');
        return enqueueOutbox({
          id: `report_${item.businessId}_${item.timestamp}`,
          entityType: 'report_sync',
          payload: item
        });
      })
      .then((row) => {
        if (row?.id) item.outboxId = row.id;
        offlineQueue.push(item);
      })
      .catch((e) => console.warn('[Outbox] enqueue report_sync failed:', e?.message || e));
  },

  /** Drain report_sync from Dexie outbox only. */
  async syncOfflineReports() {
    if (isSyncing) {
      return { success: false, error: 'Sync already in progress' };
    }

    isSyncing = true;

    try {
      await this.loadQueue();
      const queueSize = offlineQueue.length;

      const canReach = await canReachSupabase();
      if (!canReach) {
        return { success: false, error: 'Cannot reach Supabase', queueSize };
      }

      const { processPendingOutbox } = await import('../infrastructure/storage/outboxProcessor');
      const summary = await processPendingOutbox({ entityType: 'report_sync', limit: 50 });

      await this.loadQueue();

      if (process.env.NODE_ENV === 'development' && summary.processed > 0) {
        console.info('[Offline] report_sync outbox drain:', summary);
      }

      return {
        success: summary.failed === 0 || summary.done > 0,
        summary,
        successCount: summary.done,
        queueSize: offlineQueue.length
      };
    } catch (error) {
      console.error('[Offline] syncOfflineReports error:', error);
      return { success: false, error: error.message };
    } finally {
      isSyncing = false;
    }
  },

  /** Status from Outbox only (legacy KV migrated once via migrateLegacyOfflineReportsQueue). */
  async loadQueue() {
    offlineQueue = [];
    try {
      const { listPendingOutbox, migrateLegacyOfflineReportsQueue } = await import(
        '../infrastructure/storage/outboxAccess'
      );
      await migrateLegacyOfflineReportsQueue();
      const pending = await listPendingOutbox('report_sync');
      offlineQueue = pending
        .filter((row) => row?.payload?.businessId && row?.payload?.reportData)
        .map((row) => ({
          outboxId: row.id,
          businessId: row.payload.businessId,
          reportData: row.payload.reportData,
          timestamp: row.payload.timestamp || row.createdAt
        }));
    } catch (error) {
      console.warn('[Outbox] report_sync load failed:', error?.message || error);
      offlineQueue = [];
    }

    return offlineQueue.length;
  },

  getQueueStatus() {
    return {
      size: offlineQueue.length,
      items: offlineQueue.map((item) => ({
        businessId: item.businessId,
        timestamp: item.timestamp,
        hasData: !!item.reportData
      }))
    };
  }
};

// Order management: local SoT + order_sync outbox (ADR-0017 W2)
export const orderService = {
  async createOrder(orderData) {
    try {
      const bizId = orderData?.business_id || orderData?.businessId;
      const { assertMirrorSyncAllowed } = await import('./cloudCapability');
      const mirrorOk = assertMirrorSyncAllowed(bizId).allowed;

      const newOrder = storeOrderLocally({
        ...orderData,
        businessId: bizId,
        business_id: bizId
      });
      await enqueueOrderSync(bizId, newOrder, 'upsert');

      return {
        success: true,
        data: newOrder,
        localOnly: !mirrorOk,
        deferred: true
      };
    } catch (error) {
      console.error('Error creating order:', error);
      return { success: false, error: error.message };
    }
  },

  async getOrders(businessId) {
    const offlineOrders = getOfflineOrdersArray();
    if (!isSupabaseConfigured() || !shouldAllowSupabaseNetwork()) {
      return { success: true, data: offlineOrders };
    }
    try {
      const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
      const remoteId = resolveSupabaseBusinessId(businessId) || businessId;

      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('business_id', remoteId)
        .order('order_date', { ascending: false });

      if (error) throw error;
      const remote = Array.isArray(data) ? data : [];
      // Prefer local unsynced/recent over remote when same order_id
      const byKey = new Map();
      for (const r of remote) {
        const k = String(r.order_id || r.orderId || r.id || '');
        if (k) byKey.set(k, { ...r, orderId: r.order_id || r.orderId });
      }
      for (const o of offlineOrders) {
        const k = String(o.orderId || o.order_id || o.id || '');
        if (k) byKey.set(k, o);
      }
      return { success: true, data: [...byKey.values()] };
    } catch (error) {
      console.warn('[orderService] getOrders cloud failed — local:', error?.message || error);
      return { success: true, data: offlineOrders };
    }
  },

  async updateOrderStatus(orderId, status, actualDeliveryDate = null) {
    const offlineOrders = getOfflineOrdersArray();
    const existing =
      offlineOrders.find(
        (order) => order.orderId === orderId || order.order_id === orderId
      ) || {};
    const updated = storeOrderLocally({
      ...existing,
      orderId,
      status,
      actualDeliveryDate,
      actual_delivery_date: actualDeliveryDate
    });
    const bizId = updated.businessId || updated.business_id;
    await enqueueOrderSync(bizId, updated, 'upsert');
    return { success: true, deferred: true, data: updated };
  },

  async convertOrderToPurchase(orderData, purchaseData) {
    const storeLocalConversion = () => {
      const conversions = getOfflineConversionsArray();
      conversions.push({
        orderId: orderData.orderId,
        purchaseData,
        timestamp: Date.now(),
        synced: false
      });
      setOfflineConversionsArray(conversions);
    };

    storeLocalConversion();

    const orderId = orderData.orderId || orderData.order_id;
    await this.updateOrderStatus(
      orderId,
      'delivered',
      purchaseData?.deliveryDate || null
    );

    // Purchase cloud path is owned by purchases repository / outbox when persisted there
    return { success: true, deferred: true };
  },

  async deleteOrder(orderId) {
    const offlineOrders = getOfflineOrdersArray();
    const existing =
      offlineOrders.find(
        (order) => order.orderId === orderId || order.order_id === orderId
      ) || { orderId };
    const bizId = existing.businessId || existing.business_id;
    setOfflineOrdersArray(
      offlineOrders.filter(
        (order) => order.orderId !== orderId && order.order_id !== orderId
      )
    );
    await enqueueOrderSync(bizId, { ...existing, orderId }, 'delete');
    return { success: true, deferred: true };
  },

  /** Drain order_sync outbox (+ migrate legacy unsynced offline_orders into outbox). */
  async syncOfflineOrders() {
    try {
      const offlineOrders = getOfflineOrdersArray();
      const unsynced = offlineOrders.filter((order) => !order.synced);
      const enqueuedKeys = new Set();
      for (const order of unsynced) {
        const biz = order.businessId || order.business_id;
        if (!biz) continue;
        const key = String(order.orderId || order.order_id || order.id || '');
        await enqueueOrderSync(biz, order, 'upsert');
        if (key) enqueuedKeys.add(key);
      }

      const { processPendingOutbox } = await import(
        '../infrastructure/storage/outboxProcessor'
      );
      const summary = await processPendingOutbox({ entityType: 'order_sync', limit: 50 });

      // Only mark successfully drained intents (never flag failed/deferred as synced)
      if (
        enqueuedKeys.size > 0 &&
        summary.done > 0 &&
        (summary.failed || 0) === 0 &&
        (summary.deferred || 0) === 0
      ) {
        setOfflineOrdersArray(
          offlineOrders.map((order) => {
            const key = String(order.orderId || order.order_id || order.id || '');
            if (enqueuedKeys.has(key)) return { ...order, synced: true };
            return order;
          })
        );
      }

      return {
        success: summary.failed === 0 || summary.done > 0,
        syncedCount: summary.done,
        summary
      };
    } catch (error) {
      console.error('Error syncing offline orders:', error);
      return { success: false, error: error.message };
    }
  }
};

const supabaseServices = { authService, businessService, reportsService, offlineService, orderService }
export { supabase }
export default supabaseServices 